/**
 * UndoPill — floating paper pill above the staged-node reviewer.
 *
 * Shows the most recent reversible canvas action ("Added X" / "Skipped X" /
 * "Accepted N proposals" / ...) for 8 seconds with a sienna progress bar
 * that drains the timer. Hitting Undo restores the captured state.
 *
 * Brand v1.3: paper surface with strong hairline border instead of the
 * previous slate-navy floating pill — slate is now reserved for spotlight
 * moments (heroes, icons, focus mode), not floating product UI.
 *
 * Action shape lives in useProcessCanvas; this component is purely visual.
 */

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Undo2 } from 'lucide-react';
import type { UndoAction } from '@/hooks/useProcessCanvas';

interface UndoPillProps {
  action: UndoAction | null;
  onUndo: () => void;
}

const DURATION_MS = 8000;

export function UndoPill({ action, onUndo }: UndoPillProps) {
  const [progress, setProgress] = useState(1);

  useEffect(() => {
    if (!action) return;
    setProgress(1);
    const start = Date.now();
    const tick = window.setInterval(() => {
      const p = Math.max(0, 1 - (Date.now() - start) / DURATION_MS);
      setProgress(p);
      if (p <= 0) window.clearInterval(tick);
    }, 50);
    return () => window.clearInterval(tick);
  }, [action?.id]);

  return (
    <AnimatePresence>
      {action && (
        <motion.div
          key={action.id}
          role="status"
          aria-live="polite"
          className="fixed left-1/2 z-50 inline-flex items-center gap-2.5 overflow-hidden"
          initial={{ opacity: 0, x: '-50%', y: 8 }}
          animate={{ opacity: 1, x: '-50%', y: 0 }}
          exit={{ opacity: 0, x: '-50%', y: 8 }}
          transition={{ duration: 0.18, ease: 'easeInOut' }}
          style={{
            bottom: 160,
            padding: '8px 8px 8px 14px',
            background: 'hsl(var(--card))',
            color: 'hsl(var(--foreground))',
            borderRadius: 999,
            border: '1px solid hsl(var(--border-strong))',
            boxShadow: 'var(--shadow-3)',
            fontSize: 12.5,
            fontWeight: 500,
          }}
        >
      <span
        className="truncate"
        style={{ maxWidth: 300 }}
      >
        {action.label}
      </span>
      <button
        type="button"
        onClick={onUndo}
        className="inline-flex items-center gap-1 transition-colors hover:[background:hsl(var(--teal)/0.18)]"
        style={{
          padding: '5px 11px',
          borderRadius: 999,
          background: 'hsl(var(--teal) / 0.12)',
          color: 'hsl(var(--teal-dark))',
          border: '1px solid hsl(var(--teal) / 0.4)',
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: '0.02em',
          cursor: 'pointer',
        }}
      >
        <Undo2 className="h-2.5 w-2.5" />
        Undo
      </button>
      <span
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-[2px] w-full"
        style={{
          background: 'hsl(var(--sienna))',
          transform: `scaleX(${progress})`,
          transformOrigin: 'left',
          transition: 'transform 50ms linear',
        }}
      />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
